import React from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AppDatePicker from "../../shared/AppDatePicker";
import {
    CUSTOMER_SELECT_EMPTY_HELPER,
    CUSTOMER_SELECT_LABEL,
    CUSTOMER_SELECT_LOADING,
    CUSTOMER_SELECT_NO_OPTIONS,
    CUSTOMER_SELECT_PLACEHOLDER,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
} from "../../shared/customerSelectUtils";
import Autocomplete from "@mui/material/Autocomplete";

const RecurringIncomeDialog = ({
    open,
    onClose,
    onSave,
    recurringSaving,
    recurringMode,
    recurringForm,
    recurringErrors,
    customerOptions,
    loadingCustomers,
    recurringCustomerOption,
    updateRecurringForm,
    recurrenceOptions,
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
            {recurringMode === "edit"
                ? "Wiederkehrende Einnahme bearbeiten"
                : "Wiederkehrende Einnahme anlegen"}
        </DialogTitle>
        <DialogContent dividers>
            {recurringForm ? (
                <Stack spacing={2}>
                    <TextField
                        label="Titel"
                        value={recurringForm.title || ""}
                        onChange={(e) => updateRecurringForm("title", e.target.value)}
                        fullWidth
                        autoFocus
                        error={Boolean(recurringErrors.title)}
                        helperText={recurringErrors.title || ""}
                    />
                    <Autocomplete
                        options={customerOptions}
                        loading={loadingCustomers}
                        value={recurringCustomerOption}
                        onChange={(_, value) => {
                            updateRecurringForm("customerId", value?.id || "");
                            updateRecurringForm(
                                "counterparty",
                                value?.displayName || value?.label || ""
                            );
                        }}
                        getOptionLabel={getCustomerOptionLabel}
                        isOptionEqualToValue={isCustomerOptionEqualToValue}
                        loadingText={CUSTOMER_SELECT_LOADING}
                        noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={CUSTOMER_SELECT_LABEL}
                                placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                error={Boolean(recurringErrors.counterparty)}
                                helperText={
                                    recurringErrors.counterparty ||
                                    (!loadingCustomers && customerOptions.length === 0
                                        ? CUSTOMER_SELECT_EMPTY_HELPER
                                        : "")
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingCustomers ? (
                                                <CircularProgress color="inherit" size={20} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        fullWidth
                    />
                    <Grid2 container spacing={2}>
                        <Grid2 item xs={12} sm={6}>
                            <FormControl fullWidth error={Boolean(recurringErrors.recurrencePeriod)}>
                                <InputLabel id="recurrence-period-label">Rhythmus</InputLabel>
                                <Select
                                    labelId="recurrence-period-label"
                                    id="recurrence-period-select"
                                    label="Rhythmus"
                                    value={recurringForm.recurrencePeriod || "MONTHLY"}
                                    onChange={(e) => updateRecurringForm("recurrencePeriod", e.target.value)}
                                >
                                    {recurrenceOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {recurringErrors.recurrencePeriod && (
                                    <FormHelperText>{recurringErrors.recurrencePeriod}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid2>
                        <Grid2 item xs={12} sm={6}>
                            <TextField
                                label="Tag im Monat"
                                type="number"
                                inputMode="decimal"
                                inputProps={{ min: "1", max: "31", step: "1" }}
                                value={recurringForm.dayOfMonth ?? ""}
                                onChange={(e) => updateRecurringForm("dayOfMonth", e.target.value)}
                                fullWidth
                                error={Boolean(recurringErrors.dayOfMonth)}
                                helperText={recurringErrors.dayOfMonth || "1–31, leer = Startdatum"}
                            />
                        </Grid2>
                        <Grid2 item xs={12} sm={6}>
                            <AppDatePicker
                                label="Startdatum"
                                value={recurringForm.startDate || ""}
                                onChange={(date) => updateRecurringForm("startDate", date)}
                                fullWidth
                                error={Boolean(recurringErrors.startDate)}
                                helperText={recurringErrors.startDate || ""}
                            />
                        </Grid2>
                        <Grid2 item xs={12} sm={6}>
                            <AppDatePicker
                                label="Enddatum"
                                value={recurringForm.endDate || ""}
                                onChange={(date) => updateRecurringForm("endDate", date)}
                                fullWidth
                                error={Boolean(recurringErrors.endDate)}
                                helperText={recurringErrors.endDate || "Optional"}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2}>
                        <Grid2 item xs={12} sm={4}>
                            <TextField
                                label="Betrag brutto"
                                type="number"
                                inputMode="decimal"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={recurringForm.grossAmount ?? ""}
                                onChange={(e) => updateRecurringForm("grossAmount", e.target.value)}
                                fullWidth
                                error={Boolean(recurringErrors.grossAmount)}
                                helperText={recurringErrors.grossAmount || ""}
                            />
                        </Grid2>
                        <Grid2 item xs={12} sm={4}>
                            <TextField
                                label="Betrag netto"
                                type="number"
                                inputMode="decimal"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={recurringForm.netAmount ?? ""}
                                onChange={(e) => updateRecurringForm("netAmount", e.target.value)}
                                fullWidth
                                error={Boolean(recurringErrors.netAmount)}
                                helperText={recurringErrors.netAmount || ""}
                            />
                        </Grid2>
                        <Grid2 item xs={12} sm={4}>
                            <TextField
                                label="USt-Betrag"
                                type="number"
                                inputMode="decimal"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={recurringForm.vatAmount ?? ""}
                                onChange={(e) => updateRecurringForm("vatAmount", e.target.value)}
                                fullWidth
                                error={Boolean(recurringErrors.vatAmount)}
                                helperText={recurringErrors.vatAmount || ""}
                            />
                        </Grid2>
                    </Grid2>
                    <TextField
                        label="Rechnungsnr.-Muster"
                        value={recurringForm.invoiceNumberTemplate || ""}
                        onChange={(e) => updateRecurringForm("invoiceNumberTemplate", e.target.value)}
                        fullWidth
                        error={Boolean(recurringErrors.invoiceNumberTemplate)}
                        helperText={
                            recurringErrors.invoiceNumberTemplate ||
                            "Nutze %s als Platzhalter, z. B. RE-%s"
                        }
                    />
                    <AppDatePicker
                        label="Nächste Ausführung"
                        value={recurringForm.nextExecutionDate || ""}
                        onChange={(date) => updateRecurringForm("nextExecutionDate", date)}
                        fullWidth
                        error={Boolean(recurringErrors.nextExecutionDate)}
                        helperText={
                            recurringErrors.nextExecutionDate ||
                            "Optional – leer lassen für automatische Berechnung"
                        }
                    />
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Formular konnte nicht geladen werden.
                </Typography>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={recurringSaving}>
                Abbrechen
            </Button>
            <Button
                onClick={onSave}
                disabled={recurringSaving}
                variant="contained"
                sx={{ backgroundColor: "orange", color: "#000", fontWeight: 700, "&:hover": { backgroundColor: "#ff9e1a" } }}
            >
                {recurringSaving ? "Speichere…" : "Speichern"}
            </Button>
        </DialogActions>
    </Dialog>
);

export default RecurringIncomeDialog;
